import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import './JobApplicationForm.css';
import Loading from '../components/Loading';

function JobApplicationForm() {
  const { jobId } = useParams();

  const jobDescriptions = {
    'Cognitive-Knowledge-Engineer': {
      title: 'Cognitive Knowledge Engineer (AI, LLM)',
      description: 'Join our team at Digital1i as a Cognitive Knowledge Engineer, where you will design and maintain AI-powered knowledge systems using LLMs to enhance our intelligent software products.',
    },
    'Senior-AI-Systems-Engineer': {
      title: 'Senior AI Systems Engineer (LLM Inference)',
      description: 'Embark on an exciting journey with Digital1i as a Senior AI Systems Engineer, where you will build and optimize large language model inference systems, ensuring high-performance, scalable AI experiences across our products.',
    },
    'Voice-AI-Development-Expert': {
      title: 'Voice AI Development Expert',
      description: 'Join Digital1i as a Voice AI Development Expert and lead the creation of cutting-edge voice-driven applications. Leverage speech recognition, NLP, and real-time processing to build natural, responsive voice experiences.',
    },
    'Remote-Cloud-Infrastructure-Engineer': {
      title: 'Remote Cloud Infrastructure Engineer',
      description: 'Become a pivotal part of Digital1i as Remote Cloud Infrastructure Engineer, where you will design, deploy, and manage scalable cloud environments to support our growing suite of digital products.',
    },
  };

  const jobDetails = jobDescriptions[jobId];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    resume: null,
    coverLetter: null,
    linkedInProfile: '',
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({
      ...formData,
      [name]: files[0],
    });
  };

  const resetCaptcha = () => {
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      alert('Please verify that you are not a robot!');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('firstName', formData.firstName);
    formDataToSend.append('lastName', formData.lastName);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('resume', formData.resume);
    if (formData.coverLetter) {
      formDataToSend.append('coverLetter', formData.coverLetter);
    }
    formDataToSend.append('linkedInProfile', formData.linkedInProfile);
    formDataToSend.append('jobTitle', jobDetails.title); // Adding job title to form data
    formDataToSend.append('captchaToken', captchaToken);

    setIsSubmitting(true);
    try {
      const response = await fetch('/jobscript.php', {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        let errorMessage = 'There was an error sending your application. Please try again later.';
        try {
          const result = await response.json();
          if (result.message) errorMessage = result.message;
        } catch {
          // Keep the generic message if the server did not return JSON.
        }
        resetCaptcha();
        alert(errorMessage);
        return;
      }

      alert('Your application has been sent!');
      setFormData({ firstName: '', lastName: '', email: '', phone: '', resume: null, coverLetter: null, linkedInProfile: '' });
      formRef.current?.reset();
      resetCaptcha();
    } catch {
      resetCaptcha();
      alert('There was a connection error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!jobDetails) {
    return <p>Job not found.</p>;
  }

  return (
    <div id="jobapplication" className="jobapplication">
      <Loading/>
      <div className="job-application">
        <h1>{jobDetails.title}</h1>
        <p>{jobDetails.description}</p>
        <form ref={formRef} onSubmit={handleSubmit} className="job-application-form">
          <h2>Apply for this Job</h2>
          <div>
            <label>First Name *</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Last Name *</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Phone *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Resume/CV *</label>
            <input
              type="file"
              name="resume"
              accept=".pdf,.doc,.docx,.txt,.rtf"
              onChange={handleFileChange}
              required
            />
          </div>
          <div>
            <label>Cover Letter</label>
            <input
              type="file"
              name="coverLetter"
              accept=".pdf,.doc,.docx,.txt,.rtf"
              onChange={handleFileChange}
            />
          </div>
          <div>
            <label>LinkedIn Profile</label>
            <input
              type="url"
              name="linkedInProfile"
              value={formData.linkedInProfile}
              onChange={handleChange}
            />
          </div>
          <div className="job-captcha-container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LfRBpgtAAAAAPMjWmjUu3qEK-1TJ5EzOoBmuWj3"
              onChange={setCaptchaToken}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>
          <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Submit Application'}</button>
        </form>
        <div style={{ height: '2rem' }}></div>
      </div>
    </div>
  );
}

export default JobApplicationForm;
